/**
 * `trackConnectIntegration` — the one EHR-door emitter that is a v1/V2 COMPOUND.
 *
 * Split out of `./ehr-events` on purpose. The v1 sender reaches
 * `analyticsScribeModel`, `@/atoms/integrationAtoms` and `posthog-js`, i.e. the
 * Rematch model cluster; keeping it in the shared emitter module would put that
 * graph in front of every hook that only wants a V2 event, which is the coupling
 * severing the bus is supposed to remove. Callers of the light emitters pay
 * nothing for this file.
 */
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { trackV2 } from '@/lib/analytics/track-v2';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { SegmentActionName, SegmentObjectName } from '@heidi/analytics/events';
import type { components } from '@heidi/schema/ml-scribe';

/**
 * The v1 leg's `userInfo` shape. Reached through the `@heidi/schema` door rather
 * than through the `@/types` shim the model method used — same generated type,
 * and new code does not add a shim importer.
 */
type CreateEHRProfileRequest = components['schemas']['CreateEHRProfileAPIRequest'];

/**
 * Fired when an integration is connected.
 *
 * TWO EVENTS, NOT ONE, and the V2 leg carries EMPTY props while the v1 leg
 * carries the whole request under a `userInfo` key. Transcribed from the model
 * method rather than tidied — `integration_linked` has no props in the warehouse
 * today and giving it some here would be a schema change disguised as a
 * refactor. The v1 leg also has NO `options`, so it keeps its default routing.
 */
export function trackConnectIntegration(userInfo: CreateEHRProfileRequest): void {
  trackLegacyEvent({
    name: SegmentObjectName.Integrations,
    props: {
      action: SegmentActionName.IntegrationsConnectIntegration,
      userInfo: userInfo,
    },
  });

  // V2
  trackV2({
    event_name: TrackingEventName.IntegrationLinked,
    props: {},
  });
}
